/**
 * INDEX.JS - REACT ROOT + LOGTO PROVIDER
 *
 * Version v17 - OFFICIAL @logto/capacitor SDK
 * Production Logto Tenant: 5asojn.logto.app
 *
 * Verwendet automatisch:
 * - .env.development bei `npm start`
 * - .env.production bei `npm run build`
 *
 * ✅ v15: Capacitor-Erkennung für Mobile Apps
 * ✅ v17: Offizielles @logto/capacitor SDK für native Apps
 *
 * WICHTIG: Native Apps verwenden @logto/capacitor direkt in App.js,
 *          Web Apps verwenden @logto/react via LogtoProvider
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { LogtoProvider } from '@logto/react';
import { Capacitor } from '@capacitor/core';
import './index.css';
import App from './App';
import Callback from './Pages/Callback';

// ✅ v18: NativeAuthProvider für native Apps
import { NativeAuthProvider } from './context/NativeAuthContext';

// ==============================================
// AUTO ENVIRONMENT CONFIGURATION
// ==============================================

const FRONTEND_URL = process.env.REACT_APP_FRONTEND_URL;
const API_URL = process.env.REACT_APP_API_URL;

// ✅ v15: Capacitor-Erkennung für korrekte Redirect URI
const isNative = Capacitor.isNativePlatform();
const platform = Capacitor.getPlatform(); // 'android', 'ios', oder 'web'

// ✅ v15.1: Native Apps verwenden Custom URL Scheme
// Android: de.planivio.app://callback
// iOS: capacitor://localhost/callback
const getRedirectUri = () => {
  if (isNative) {
    if (platform === 'android') {
      // Custom URL Scheme für Android Deep Links
      return 'de.planivio.app://callback';
    } else if (platform === 'ios') {
      return 'capacitor://localhost/callback';
    }
  }
  return `${FRONTEND_URL}/callback`;
};

const getPostLogoutUri = () => {
  if (isNative) {
    if (platform === 'android') {
      return 'de.planivio.app://callback';
    } else if (platform === 'ios') {
      return 'capacitor://localhost/callback';
    }
  }
  return FRONTEND_URL;
};

const logtoConfig = {
  endpoint: process.env.REACT_APP_LOGTO_ENDPOINT,
  appId: process.env.REACT_APP_LOGTO_APP_ID,
  resources: [process.env.REACT_APP_LOGTO_API_RESOURCE || API_URL],
  scopes: [
    'openid',
    'profile',
    'email',
    'offline_access'
  ]
};

// Redirect Configuration - ✅ v15: Dynamisch basierend auf Plattform
export const REDIRECT_CONFIG = {
  redirectUri: getRedirectUri(),
  postLogoutRedirectUri: getPostLogoutUri()
};

// API Configuration
export const API_CONFIG = {
  baseURL: API_URL,
  timeout: 10000
};

// ==============================================
// ENVIRONMENT LOGGING
// ==============================================

const envMode = process.env.REACT_APP_ENV || process.env.NODE_ENV;

if (process.env.NODE_ENV !== 'production') {
  console.log(`PLANIVIO FRONTEND v15 - ${envMode?.toUpperCase()}`);
  console.log('Platform:', platform, isNative ? '(Native)' : '(Web)');
  console.log('Endpoint:', logtoConfig.endpoint);
  console.log('Frontend:', FRONTEND_URL);
  console.log('Redirect URI:', REDIRECT_CONFIG.redirectUri);
}

// ==============================================
// VALIDATION
// ==============================================

if (!FRONTEND_URL || !API_URL) {
  console.error('❌ FEHLER: Environment Variables nicht gesetzt!');
  console.error('   Prüfe .env.development oder .env.production');
}

// ==============================================
// ROOT RENDER
// ==============================================

const root = ReactDOM.createRoot(document.getElementById('root'));

if (process.env.NODE_ENV !== 'production') {
  console.log('Auth Mode:', isNative ? '@logto/capacitor (Native)' : '@logto/react (Web)');
}

// ✅ v18: WICHTIG - Native und Web haben unterschiedliche Auth-Flows!
// - Native Apps: @logto/capacitor handled alles intern (KEIN LogtoProvider!)
// - Web Apps: @logto/react mit LogtoProvider + Callback-Route

if (isNative) {
  // ═══════════════════════════════════════════════════════════════
  // NATIVE APP (Android/iOS) - Verwendet @logto/capacitor
  // NativeAuthProvider stellt die gleiche API wie useLogto bereit
  // KEIN LogtoProvider, da es Konflikte mit @logto/capacitor gibt!
  // ═══════════════════════════════════════════════════════════════
  if (process.env.NODE_ENV !== 'production') console.log('Native Mode: NativeAuthProvider');

  root.render(
    <BrowserRouter>
      <NativeAuthProvider>
        <App />
      </NativeAuthProvider>
    </BrowserRouter>
  );
} else {
  // ═══════════════════════════════════════════════════════════════
  // WEB APP - Verwendet @logto/react
  // LogtoProvider + useLogto hooks + Callback-Route
  // ═══════════════════════════════════════════════════════════════
  if (process.env.NODE_ENV !== 'production') console.log('Web Mode: LogtoProvider');

  root.render(
    <BrowserRouter>
      <LogtoProvider config={logtoConfig}>
        <Routes>
          <Route path="/callback" element={<Callback />} />
          <Route path="/*" element={<App />} />
        </Routes>
      </LogtoProvider>
    </BrowserRouter>
  );
}